import { z } from 'zod';
import { AccountPurpose, AccountRole, AccountStatus, WarmupPhase } from '../enums.js';
import { CursorPagination } from './common.js';
export const AccountSummary = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    phone: z.string(),
    proxyId: z.string().uuid().nullable(),
    role: AccountRole,
    purpose: AccountPurpose,
    status: AccountStatus,
    warmupPhase: WarmupPhase,
    healthScore: z.number().int(),
    ageDays: z.number().int(),
    dailyDmCount: z.number().int(),
    lastPassiveSignalAt: z.string().datetime().nullable(),
    lastActiveProbeAt: z.string().datetime().nullable(),
    // TG profile snapshot — populated on first spawn / manual refresh.
    // Nullable on freshly-imported accounts that haven't spawned yet.
    tgUserId: z.string().nullable(),
    tgUsername: z.string().nullable(),
    tgFirstName: z.string().nullable(),
    tgLastName: z.string().nullable(),
    tgProfileFetchedAt: z.string().datetime().nullable(),
    /** Live mtcute connection state.
     *   true  → a handle is currently held in TgRuntimeService.handles
     *   false → never spawned, evicted, or spawn failed
     *  NOTE: still optimistic — a held handle whose underlying socket has
     *  silently died will report true until next reconcile (~30s) catches it.
     *  See ProxyService.healthCheck for the network-level truth. */
    isConnected: z.boolean(),
    /** Resource-discovery FLOOD cooldown (ISO) — the account still ingests,
     *  but the discovery worker skips it until then. Null = none. */
    discoveryCooldownUntil: z.string().datetime().nullable().optional(),
    /** Operator pause flag — when true reconcile won't (re-)spawn this
     *  account. Drives the 上线/下线 button in the admin UI. */
    adminPaused: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const RefreshAccountProfileResponse = z.object({
    account: AccountSummary,
});
export const ListAccountsQuery = CursorPagination.extend({
    status: AccountStatus.optional(),
    warmupPhase: WarmupPhase.optional(),
});
// ---------- TG login wizard ----------
export const StartAccountLoginRequest = z.object({
    phone: z.string().regex(/^\+?\d{8,15}$/),
    proxyId: z.string().uuid(),
    tenantId: z.string().uuid(),
    /** observer / sender. Defaults to 'observer' at the wire boundary so older
     *  callers keep working — admin UI pre-selects via the form radio. */
    purpose: AccountPurpose.optional().default('observer'),
});
export const StartAccountLoginResponse = z.object({
    loginAttemptId: z.string().uuid(),
    expiresAt: z.string().datetime(),
});
export const SubmitLoginCodeRequest = z.object({
    loginAttemptId: z.string().uuid(),
    code: z.string().regex(/^\d{5}$/),
});
export const SubmitLoginCodeResponse = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('signed_in'),
        account: AccountSummary,
    }),
    z.object({
        status: z.literal('need_2fa'),
        loginAttemptId: z.string().uuid(),
    }),
]);
export const SubmitTwoFaPasswordRequest = z.object({
    loginAttemptId: z.string().uuid(),
    password: z.string().min(1),
});
export const SubmitTwoFaPasswordResponse = z.object({
    account: AccountSummary,
});
export const UpdateAccountWarmupRequest = z.object({
    warmupPhase: WarmupPhase,
});
// ---------- Direct session-string import (skip TG auth) ----------
/**
 * Import a pre-existing mtcute string session, bypassing the SMS+code flow.
 *
 * Use case: operator already has working sessions from a prior tooling
 * round (or a recovered session from another instance) and just wants to
 * register them in the system. We store the session under KMS encryption
 * exactly the same as the SMS-flow path. Phone is recorded for ops sanity
 * (matching the number the session was minted for).
 *
 * No round-trip with TG happens here — the session's validity is verified
 * lazily when modules/tg-runtime first spawn()s a client. A broken session
 * surfaces as the standard `AUTH_KEY_UNREGISTERED` → account.status='banned'.
 */
export const ImportAccountSessionRequest = z.object({
    phone: z.string().regex(/^\+?\d{8,15}$/),
    proxyId: z.string().uuid(),
    tenantId: z.string().uuid(),
    /** observer / sender. Defaults to 'observer' at the wire boundary so older
     *  callers keep working — admin UI pre-selects via the form radio. */
    purpose: AccountPurpose.optional().default('observer'),
    /** mtcute StringSession (base64-ish). We trust it; validation is lazy. */
    sessionString: z.string().min(1),
    /**
     * Optional 2FA cloud password — needed for some account operations
     * (logout, change password). Stored KMS-encrypted. If omitted, account
     * lives without 2FA password on file (most operations still work).
     */
    twofaPassword: z.string().optional(),
});
export const ImportAccountSessionResponse = z.object({
    account: AccountSummary,
});
//# sourceMappingURL=account.js.map